import {
  ATTENDANCE_STATUS_LABEL_PT_BR,
  AttendanceStatus,
  IshikawaCategory,
  ISHIKAWA_CATEGORY_LABEL_PT_BR,
  Permission,
  type CreateCapaActionInput,
  type SignedCommand,
  type UpdateMeetingInput,
} from '@qualicloud/contracts';
import { MeetingStatus } from '@qualicloud/domain';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { useState, type ComponentProps } from 'react';
import { SignatureDialog } from '@/components/signature/signature-dialog';
import { PrintableRecord, SignatureManifest } from '@/components/signature/signature-manifest';
import { CapaActionDialog } from '@/components/sgq/capa-action-dialog';
import { IshikawaBoard } from '@/components/sgq/ishikawa-board';
import { PeoplePicker } from '@/components/sgq/people-picker';
import {
  AttendanceStatusBadge,
  MeetingStatusBadge,
  NonconformityStatusBadge,
} from '@/components/sgq/sgq-badges';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Checkbox,
  Dialog,
  EmptyState,
  Field,
  IconAlertTriangle,
  IconCalendar,
  IconCheckCircle,
  IconInfo,
  IconPrinter,
  IconUsers,
  Input,
  LoadingRegion,
  NumberInput,
  RadioGroup,
  Select,
  Skeleton,
  Tabs,
  Textarea,
  useToast,
} from '@/design-system';
import {
  capaApi,
  meetingsApi,
  nonconformitiesApi,
  queryKeys,
  saveBlob,
  sgqSignedActInvalidations,
  toUserMessage,
  requestTypesApi,
} from '@/lib/api';
import type { MeetingDetail, MeetingParticipant } from '@/lib/api/sgq.types';
import { formatDateTime, formatRegulatedDateTime, truncateHash } from '@/lib/format/datetime';
import { signedActRecordId } from '@/lib/signed-act-record';
import { MEETING_STATUS_LABEL } from '@/lib/sgq/presentation';
import { useDirectory } from '@/lib/use-directory';
import { useAuth, useOrganizationTimeZone } from '@/providers/auth-provider';
import { PageBody, PageHeader } from '@/shell/app-shell';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * CONDUÇÃO DA REUNIÃO SACAP
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * É a tela onde o ciclo do SGQ efetivamente acontece: pauta, presença, análise
 * de causa (Ishikawa), promoção de causa a ação corretiva 5W2H e, no fim, a ata
 * assinada.
 *
 * O QUE ISTO CORRIGE
 *  · o `finish()` do legado NÃO VALIDAVA NADA: não checava pauta,
 *    participantes, tarefas em aberto, permissão, nem se a reunião já estava
 *    encerrada — e o `UPDATE dt_encerramento = NOW()` era idempotente-destrutivo
 *    (§B.3). Aqui encerrar é ato assinado que passa por
 *    `assertMeetingCanBeClosed`;
 *  · a ata só existia como corpo de e-mail. Agora é registro append-only,
 *    congelado no ato, com manifesto de assinatura;
 *  · confirmar presença devolvia sucesso só para "confirmo": recusar um convite
 *    parecia falha. `AttendanceStatus` cobre os cinco estados reais.
 */

type PendingAct = { readonly kind: 'close'; readonly minutes: string };

export function MeetingDetailPage() {
  const { meetingId } = useParams({ strict: false }) as { meetingId: string };
  const [tab, setTab] = useState('pauta');
  const [pendingAct, setPendingAct] = useState<PendingAct | null>(null);
  const [closureOpen, setClosureOpen] = useState(false);
  const [effectivenessOpen, setEffectivenessOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [causeDialog, setCauseDialog] = useState<IshikawaCategory | null>(null);
  const [actionDialog, setActionDialog] = useState<{
    readonly rootCauseId: string;
    readonly description: string;
  } | null>(null);

  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { can, user } = useAuth();
  const timeZone = useOrganizationTimeZone();
  const directory = useDirectory();

  /*
   * Catálogo de tipos de solicitação, para o diálogo de criação a partir da
   * causa. Só os ativos: escolher um tipo aposentado num registro novo é
   * engano que o servidor recusa, e recusar depois de preencher é pior.
   */
  const requestTypesQuery = useQuery({
    queryKey: queryKeys.requestTypes.all(),
    queryFn: () => requestTypesApi.list(),
  });
  const requestTypes = requestTypesQuery.data ?? [];

  const meetingQuery = useQuery({
    queryKey: queryKeys.meetings.detail(meetingId),
    queryFn: () => meetingsApi.detail(meetingId),
  });

  const meeting = meetingQuery.data;

  /** Ishikawa por NC: cada não-conformidade tem o seu caso CAPA. */
  const [selectedNcId, setSelectedNcId] = useState<string | null>(null);
  const activeNcId = selectedNcId ?? meeting?.nonconformities[0]?.nonconformityId ?? null;

  const ncQuery = useQuery({
    queryKey: queryKeys.nonconformities.detail(activeNcId ?? ''),
    queryFn: () => nonconformitiesApi.detail(activeNcId as string),
    enabled: Boolean(activeNcId),
  });

  const capaCaseId = ncQuery.data?.capaCaseId ?? null;

  const capaQuery = useQuery({
    queryKey: queryKeys.capa.case(capaCaseId ?? ''),
    queryFn: () => capaApi.case(capaCaseId as string),
    enabled: Boolean(capaCaseId),
  });

  const invalidateSgq = async () => {
    await Promise.all(
      sgqSignedActInvalidations().map((key) => queryClient.invalidateQueries({ queryKey: key })),
    );
    await queryClient.invalidateQueries({ queryKey: queryKeys.meetings.detail(meetingId) });
  };

  const attendance = useMutation({
    mutationFn: (params: { readonly status: AttendanceStatus; readonly userId?: string }) =>
      meetingsApi.setAttendance(meetingId, {
        status: params.status,
        ...(params.userId ? { userId: params.userId } : {}),
      }),
    onSuccess: () => void invalidateSgq(),
    onError: (error) =>
      toast({
        tone: 'error',
        title: 'Não foi possível registrar a presença',
        description: toUserMessage(error),
      }),
  });

  const open = useMutation({
    mutationFn: () => meetingsApi.open(meetingId),
    onSuccess: () => {
      void invalidateSgq();
      toast({
        tone: 'success',
        title: 'Reunião aberta',
        description:
          'A abertura é ato explícito e fica registrada.',
      });
    },
    onError: (error) =>
      toast({
        tone: 'error',
        title: 'Não foi possível abrir a reunião',
        description: toUserMessage(error),
      }),
  });

  const closeMeeting = useMutation({
    mutationFn: (params: { readonly command: SignedCommand; readonly minutes: string }) =>
      meetingsApi.close(meetingId, {
        ...params.command,
        minutes: params.minutes,
        attendance: (meeting?.participants ?? [])
          .filter(
            (participant) =>
              participant.status === AttendanceStatus.ATTENDED ||
              participant.status === AttendanceStatus.ABSENT,
          )
          // O schema aceita apenas presença EFETIVA no encerramento: confirmar
          // convite e comparecer são fatos diferentes, e a ata registra o
          // segundo. O filtro acima já garante o par; o tipo apenas o declara.
          .map((participant) => ({
            userId: participant.userId,
            status: participant.status as 'ATTENDED' | 'ABSENT',
          })),
        ...(meeting?.recordHash ? { expectedRecordHash: meeting.recordHash } : {}),
      }),
  });

  const effectiveness = useMutation({
    mutationFn: (params: { readonly effective: boolean; readonly justification: string }) =>
      meetingsApi.recordEffectiveness(meetingId, params),
    onSuccess: () => {
      setEffectivenessOpen(false);
      void invalidateSgq();
      toast({
        tone: 'success',
        title: 'Eficácia da SACAP registrada',
        description:
          'A justificativa é obrigatória nos dois caminhos — é o registro de que a análise crítica aconteceu (ISO 13485 §8.5.2).',
      });
    },
    onError: (error) =>
      toast({
        tone: 'error',
        title: 'Não foi possível registrar a eficácia',
        description: toUserMessage(error),
      }),
  });

  /**
   * EDIÇÃO — o chamador que faltava para `PATCH /meetings/:id`.
   *
   * ⚠️ `nonconformityIds` NÃO é enviado. Ausente significa "não mexi nisso", e é
   * a guarda anti-wipe do BUG-REU-07: no legado, `updateReuniao` sem `nc[]` no
   * POST chamava `syncNcToReuniao(id, array())` e apagava todos os vínculos de
   * NC em silêncio. O vínculo NC↔SACAP se edita na aba própria, com o campo
   * explicitamente preenchido — nunca de carona num formulário de pauta.
   */
  const update = useMutation({
    mutationFn: (input: UpdateMeetingInput) => meetingsApi.update(meetingId, input),
    onSuccess: () => {
      setEditOpen(false);
      void invalidateSgq();
      toast({
        tone: 'success',
        title: 'Reunião atualizada',
        description:
          'Os vínculos de não-conformidade foram PRESERVADOS: campo ausente significa "não mexi nisso".',
      });
    },
    onError: (error) =>
      toast({
        tone: 'error',
        title: 'Não foi possível salvar a reunião',
        description: toUserMessage(error),
      }),
  });

  /** Cancelamento com justificativa obrigatória — §B.1 métodos 2 e 3. */
  const cancelMeeting = useMutation({
    mutationFn: (params: { readonly justification: string }) =>
      meetingsApi.cancel(meetingId, params),
    onSuccess: () => {
      setCancelOpen(false);
      void invalidateSgq();
      toast({
        tone: 'success',
        title: 'Reunião cancelada',
        description:
          'Os participantes foram notificados com a justificativa.',
      });
    },
    onError: (error) =>
      toast({
        tone: 'error',
        title: 'Não foi possível cancelar a reunião',
        description: toUserMessage(error),
      }),
  });

  /** Ata em PDF, do servidor: com manifesto de assinaturas e trilha. */
  const exportPdf = useMutation({
    mutationFn: async () => {
      const file = await meetingsApi.exportPdf(meetingId);
      saveBlob(file.blob, file.fileName);
    },
    onError: (error) =>
      toast({
        tone: 'error',
        title: 'Não foi possível exportar a ata',
        description: toUserMessage(error),
      }),
  });

  const createCause = useMutation({
    mutationFn: (params: {
      readonly category: IshikawaCategory;
      readonly description: string;
      readonly priority: number;
      readonly isRootCause: boolean;
    }) =>
      capaApi.createRootCause(capaCaseId as string, {
        category: params.category,
        description: params.description,
        priority: params.priority,
        isRootCause: params.isRootCause,
        meetingId,
      }),
    onSuccess: () => {
      setCauseDialog(null);
      void queryClient.invalidateQueries({ queryKey: queryKeys.capa.all() });
    },
    onError: (error) =>
      toast({
        tone: 'error',
        title: 'Não foi possível registrar a causa',
        description: toUserMessage(error),
      }),
  });

  const reprioritize = useMutation({
    mutationFn: (params: { readonly causeId: string; readonly priority: number }) =>
      capaApi.updateRootCause(params.causeId, { priority: params.priority }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.capa.all() }),
    onError: (error) =>
      toast({
        tone: 'error',
        title: 'Não foi possível reordenar a causa',
        description: toUserMessage(error),
      }),
  });

  const deleteCause = useMutation({
    mutationFn: (causeId: string) => capaApi.deleteRootCause(causeId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.capa.all() }),
    onError: (error) =>
      toast({
        tone: 'error',
        title: 'Não foi possível remover a causa',
        description: toUserMessage(error),
      }),
  });

  const createAction = useMutation({
    mutationFn: (input: CreateCapaActionInput) => capaApi.createAction(input),
    onSuccess: () => {
      setActionDialog(null);
      void invalidateSgq();
      toast({
        tone: 'success',
        title: 'Ação corretiva criada',
        description:
          'Os sete campos do 5W2H foram gravados.',
      });
    },
    onError: (error) =>
      toast({
        tone: 'error',
        title: 'Não foi possível criar a ação',
        description: toUserMessage(error),
      }),
  });

  if (meetingQuery.isPending) {
    return (
      <LoadingRegion loading label="Carregando a reunião…">
        <div className="p-5">
          <Skeleton className="h-8 w-80" />
          <Skeleton className="mt-3 h-4 w-96" />
          <Skeleton className="mt-6 h-72 w-full" />
        </div>
      </LoadingRegion>
    );
  }

  if (!meeting) {
    return (
      <PageBody>
        <Card>
          <EmptyState
            icon={<IconCalendar size={22} />}
            title="Reunião não encontrada"
            description="A reunião não existe ou você não é participante nem organizador. Registros de outras organizações são sempre tratados como inexistentes."
            action={
              <Button asChild variant="primary">
                <Link to="/reunioes">Voltar à lista</Link>
              </Button>
            }
          />
        </Card>
      </PageBody>
    );
  }

  const myParticipation = meeting.participants.find(
    (participant) => participant.userId === user?.id,
  );
  const canManage = can(Permission.MEETING_MANAGE);
  const canOpen = canManage && meeting.status === MeetingStatus.SCHEDULED;
  const canClose =
    canManage &&
    (meeting.status === MeetingStatus.IN_PROGRESS ||
      meeting.status === MeetingStatus.AWAITING_CLOSURE);
  const canRecordEffectiveness =
    canManage && meeting.status === MeetingStatus.AWAITING_EFFECTIVENESS;
  const editable =
    meeting.status === MeetingStatus.SCHEDULED || meeting.status === MeetingStatus.IN_PROGRESS;
  /*
   * Editar e cancelar seguem `assertCanAct` do servidor (§B.3 `valida()`:
   * participante OU emitente) — a tela só evita oferecer o que o servidor
   * recusaria. Encerrada ou cancelada, a reunião é registro: a ata assinada
   * afirma o que aconteceu, e mexer depois faria a ata mentir.
   */
  const isParticipantOrOrganizer =
    meeting.organizerUserId === user?.id || Boolean(myParticipation);
  const canEdit = isParticipantOrOrganizer && editable;
  const canCancel =
    canManage &&
    isParticipantOrOrganizer &&
    (meeting.status === MeetingStatus.SCHEDULED ||
      meeting.status === MeetingStatus.IN_PROGRESS ||
      meeting.status === MeetingStatus.AWAITING_CLOSURE);

  const ishikawaGroups = capaQuery.data?.ishikawa ?? [];

  return (
    <>
      <PageHeader
        breadcrumbs={[{ label: 'Reuniões', to: '/reunioes' }, { label: meeting.subject }]}
        title={meeting.subject}
        description={`${meeting.typeLabel} · ${meeting.location} · ${formatDateTime(
          meeting.startsAt,
          timeZone,
        )} às ${formatDateTime(meeting.endsAt, timeZone).slice(-5)}`}
        actions={
          <>
            {canOpen ? (
              <Button variant="primary" loading={open.isPending} onClick={() => open.mutate()}>
                Abrir reunião
              </Button>
            ) : null}
            {canClose ? (
              <Button
                variant="primary"
                leadingIcon={<IconCheckCircle size={15} />}
                onClick={() => setClosureOpen(true)}
              >
                Encerrar e emitir ata
              </Button>
            ) : null}
            {canRecordEffectiveness ? (
              <Button variant="primary" onClick={() => setEffectivenessOpen(true)}>
                Avaliar eficácia da SACAP
              </Button>
            ) : null}
            {/*
              EDIÇÃO — `PATCH /meetings/:id` existia e não tinha chamador
              nenhum: não havia como corrigir pauta, local, horário,
              participantes ou NCs vinculadas depois de criada, e a guarda
              anti-wipe (D-MEET-01) nunca era exercida pelo produto.
            */}
            {canEdit ? (
              <Button variant="secondary" onClick={() => setEditOpen(true)}>
                Editar reunião
              </Button>
            ) : null}
            {/*
              CANCELAMENTO — §B.1 métodos 2 e 3. Estado, colunas, CHECK e
              arestas existiam; o caminho, não. Reunião agendada por engano
              ficava no sistema para sempre.
            */}
            {canCancel ? (
              <Button variant="danger" onClick={() => setCancelOpen(true)}>
                Cancelar reunião
              </Button>
            ) : null}
            {/*
              A ata sai do SERVIDOR, com manifesto de assinaturas (§11.50(b)) e
              evento `EXPORT` na trilha. `window.print()` imprimia o navegador —
              sem manifesto, sem carimbo e sem rastro.
            */}
            <Button
              variant="secondary"
              leadingIcon={<IconPrinter size={15} />}
              loading={exportPdf.isPending}
              onClick={() => exportPdf.mutate()}
            >
              Exportar ata em PDF
            </Button>
          </>
        }
      >
        <div className="flex flex-wrap items-center gap-3">
          <MeetingStatusBadge status={meeting.status} />
          <Badge tone="outline" srPrefix="Tipo:">
            {meeting.typeLabel}
          </Badge>
          <Badge tone="outline" srPrefix="Presenças confirmadas:">
            {meeting.confirmedCount} de {meeting.participantCount}
          </Badge>
          {meeting.isFollowUp ? (
            <Badge tone="warning" icon={<IconAlertTriangle size={12} />}>
              SACAP de follow-up
            </Badge>
          ) : null}
          {meeting.effective === true ? <Badge tone="success">SACAP eficaz</Badge> : null}
          {meeting.effective === false ? <Badge tone="danger">SACAP não eficaz</Badge> : null}
        </div>
      </PageHeader>

      <PageBody className="flex flex-col gap-6">
        {myParticipation && editable ? (
          <MyAttendanceCard
            participation={myParticipation}
            busy={attendance.isPending}
            onRespond={(status) => attendance.mutate({ status })}
          />
        ) : null}

        <Tabs
          ariaLabel="Seções da reunião"
          value={tab}
          onValueChange={setTab}
          tabs={[
            {
              value: 'pauta',
              label: 'Pauta',
              content: <AgendaTab meeting={meeting} timeZone={timeZone} />,
            },
            {
              value: 'participantes',
              label: 'Participantes',
              count: meeting.participants.length,
              content: (
                <ParticipantsTab
                  meeting={meeting}
                  canManage={canManage && editable}
                  busy={attendance.isPending}
                  onSetAttendance={(userId, status) => attendance.mutate({ status, userId })}
                />
              ),
            },
            {
              value: 'ncs',
              label: 'Não-conformidades',
              count: meeting.nonconformities.length,
              content: <LinkedNonconformitiesTab meeting={meeting} />,
            },
            {
              value: 'ishikawa',
              label: 'Análise de causa',
              count: ishikawaGroups.reduce((sum, group) => sum + group.causes.length, 0),
              content: (
                <IshikawaTab
                  meeting={meeting}
                  activeNcId={activeNcId}
                  onSelectNc={setSelectedNcId}
                  groups={ishikawaGroups}
                  loading={capaQuery.isPending && Boolean(capaCaseId)}
                  hasCase={Boolean(capaCaseId)}
                  readOnly={!canManage || !editable}
                  busy={
                    reprioritize.isPending || deleteCause.isPending || createCause.isPending
                  }
                  onAdd={setCauseDialog}
                  onReprioritize={(causeId, priority) =>
                    reprioritize.mutate({ causeId, priority })
                  }
                  onPromote={(cause) =>
                    setActionDialog({ rootCauseId: cause.id, description: cause.description })
                  }
                  onDelete={(cause) => deleteCause.mutate(cause.id)}
                />
              ),
            },
            {
              value: 'ata',
              label: 'Ata',
              count: meeting.minutes.length,
              content: <MinutesTab meeting={meeting} timeZone={timeZone} />,
            },
          ]}
        />

        {/*
          Seção de primeiro nível da tela, como as abas: h2. Deixar h3 aqui
          abriria buraco na hierarquia sempre que o cartão "Sua presença" não
          aparecesse — que é o caso na maior parte do ciclo da reunião.
        */}
        <SignatureManifest
          entries={[...meeting.signatures]}
          recordLabelFallback={meeting.subject}
          headingLevel={2}
        />

        <PrintableRecord
          title={meeting.subject}
          subtitle={`${meeting.typeLabel} · ${meeting.location}`}
          metadata={[
            { label: 'Situação', value: MEETING_STATUS_LABEL[meeting.status] },
            { label: 'Início', value: formatRegulatedDateTime(meeting.startsAt, timeZone) },
            { label: 'Término', value: formatRegulatedDateTime(meeting.endsAt, timeZone) },
            { label: 'Organizador', value: meeting.organizerName ?? '—' },
            {
              label: 'Participantes',
              value: meeting.participants
                .map(
                  (participant) =>
                    `${participant.userName ?? participant.userId} (${ATTENDANCE_STATUS_LABEL_PT_BR[participant.status]})`,
                )
                .join('; '),
            },
            {
              label: 'Não-conformidades tratadas',
              value:
                meeting.nonconformities.map((item) => item.code).join(', ') ||
                'Nenhuma vinculada',
            },
            { label: 'Pauta', value: meeting.agenda },
            {
              label: 'Ata',
              value: meeting.minutes[0]?.content ?? 'Reunião ainda não encerrada',
            },
            { label: 'Impressão em', value: formatRegulatedDateTime(new Date(), timeZone) },
          ]}
          entries={[...meeting.signatures]}
        />
      </PageBody>

      {/*
        ── Edição da reunião ─────────────────────────────────────────────
        MONTADO SOB CONDIÇÃO, e isso não é detalhe de estilo.

        O diálogo guarda o rascunho em `useState` e tinha um `if (next) reset()`
        no `onOpenChange` — código morto: o `Dialog` é sempre controlado e o
        Radix só chama `onOpenChange(true)` a partir de um `DialogTrigger`, que
        não é usado em lugar nenhum deste sistema. A abertura vem de
        `setEditOpen(true)`, que não passa pelo primitivo, então o `useState`
        inicializava uma única vez e nunca mais acompanhava a reunião.

        O efeito era pior do que rascunho velho reaparecendo: o `submit` monta o
        diff contra a reunião FRESCA, então um campo estagnado na memória do
        componente virava alteração deliberada — entrava na trilha de auditoria
        como ato do usuário e revertia, em silêncio, a alteração de outra pessoa.

        Montando sob condição, cada abertura é uma instância nova e o problema
        deixa de existir. Vale o mesmo para os três diálogos abaixo.
      */}
      {editOpen ? (
      <MeetingEditDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        meeting={meeting}
        busy={update.isPending}
        people={directory.subjects.map((subject) => ({
          id: subject.userId,
          name: subject.displayName,
          hint: subject.jobTitle,
        }))}
        onConfirm={(input) => update.mutate(input)}
      />
      ) : null}

      {/* ── Cancelamento (§B.1 justifica()/cancelar()) ──────────────────── */}
      {cancelOpen ? (
      <MeetingCancelDialog
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        subject={meeting.subject}
        busy={cancelMeeting.isPending}
        onConfirm={(justification) => cancelMeeting.mutate({ justification })}
      />
      ) : null}

      {/* ── Encerramento com ata ────────────────────────────────────────── */}
      {closureOpen ? (
      <ClosureDialog
        open={closureOpen}
        onOpenChange={setClosureOpen}
        meeting={meeting}
        onConfirm={(minutes) => {
          setClosureOpen(false);
          setPendingAct({ kind: 'close', minutes });
        }}
      />
      ) : null}

      {/* ── Eficácia da SACAP ───────────────────────────────────────────── */}
      <EffectivenessDialog
        open={effectivenessOpen}
        onOpenChange={setEffectivenessOpen}
        busy={effectiveness.isPending}
        onConfirm={(params) => effectiveness.mutate(params)}
      />

      {/*
        ── Nova causa Ishikawa ───────────────────────────────────────────
        Sob condição pelo mesmo motivo dos de cima: montado sempre, o diálogo
        reabria com a descrição da causa anterior no textarea e com o botão já
        habilitado — e a prioridade nascia congelada no valor da primeira vez.
      */}
      {causeDialog !== null ? (
      <RootCauseDialog
        open={causeDialog !== null}
        category={causeDialog}
        busy={createCause.isPending}
        nextPriority={
          (ishikawaGroups.find((group) => group.category === causeDialog)?.causes.length ?? 0) + 1
        }
        onOpenChange={(next) => {
          if (!next) setCauseDialog(null);
        }}
        onConfirm={(params) => createCause.mutate(params)}
      />
      ) : null}

      {/* ── Promoção de causa a ação 5W2H ───────────────────────────────── */}
      {actionDialog ? (
        <CapaActionDialog
          open
          onOpenChange={(next) => {
            if (!next) setActionDialog(null);
          }}
          requestTypes={requestTypes}
          rootCauseId={actionDialog.rootCauseId}
          rootCauseDescription={actionDialog.description}
          users={directory.subjects.map((subject) => ({
            id: subject.userId,
            name: subject.displayName,
            hint: subject.jobTitle,
          }))}
          departments={directory.departments.map((department) => ({
            id: department.id,
            name: department.name,
          }))}
          submitting={createAction.isPending}
          submitError={createAction.isError ? toUserMessage(createAction.error) : null}
          // `mutateAsync` devolve o agregado; o diálogo só quer saber se
          // terminou. Descartar o valor aqui mantém a rejeição propagando —
          // é ela que faz o diálogo exibir o erro em vez de fechar calado.
          onSubmit={async (input) => {
            await createAction.mutateAsync(input);
          }}
        />
      ) : null}

      {/* ── Ato assinado: encerramento ──────────────────────────────────── */}
      {pendingAct ? (
        <SignatureDialog
          open
          onOpenChange={(next) => {
            if (!next) setPendingAct(null);
          }}
          act="MEETING_CLOSE"
          recordId={
            signedActRecordId('MEETING_CLOSE', {
              documentVersionId: null,
              acknowledgementId: null,
              meetingId: meeting.id,
            }) ?? ''
          }
          recordLabel={meeting.subject}
          recordDetails={[
            { label: 'Tipo', value: meeting.typeLabel },
            { label: 'Início', value: formatRegulatedDateTime(meeting.startsAt, timeZone) },
            { label: 'Participantes', value: String(meeting.participantCount) },
            {
              label: 'Não-conformidades tratadas',
              value: String(meeting.nonconformities.length),
            },
            { label: 'Impressão digital do registro', value: truncateHash(meeting.recordHash) },
          ]}
          confirmLabel="Encerrar e assinar a ata"
          onSign={async (command) => {
            await closeMeeting.mutateAsync({ command, minutes: pendingAct.minutes });
          }}
          onSigned={() => {
            setPendingAct(null);
            void invalidateSgq();
            toast({
              tone: 'success',
              title: 'Ata emitida e assinada',
              description:
                'O texto da ata foi congelado no ato e não pode ser alterado. Sendo uma SACAP, a reunião passa a aguardar a avaliação de eficácia.',
            });
          }}
        />
      ) : null}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Minha presença
// ─────────────────────────────────────────────────────────────────────────────

function MyAttendanceCard({
  participation,
  busy,
  onRespond,
}: {
  readonly participation: MeetingParticipant;
  readonly busy: boolean;
  readonly onRespond: (status: AttendanceStatus) => void;
}) {
  const responded =
    participation.status !== AttendanceStatus.INVITED;

  return (
    <Card className="border-l-4 border-l-brand">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-content">Sua presença</h2>
          <p className="mt-1 text-sm text-content-muted">
            {responded
              ? `Você respondeu ao convite: ${ATTENDANCE_STATUS_LABEL_PT_BR[participation.status]}. Pode mudar a resposta enquanto a reunião não for encerrada.`
              : 'Confirme ou recuse o convite. Recusar não é falha: a recusa fica registrada como resposta.'}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <AttendanceStatusBadge status={participation.status} />
          <Button
            variant={participation.status === AttendanceStatus.CONFIRMED ? 'primary' : 'secondary'}
            disabled={busy}
            onClick={() => onRespond(AttendanceStatus.CONFIRMED)}
          >
            Confirmar presença
          </Button>
          <Button
            variant="secondary"
            disabled={busy}
            onClick={() => onRespond(AttendanceStatus.DECLINED)}
          >
            Recusar convite
          </Button>
        </div>
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Abas
// ─────────────────────────────────────────────────────────────────────────────

function AgendaTab({
  meeting,
  timeZone,
}: {
  readonly meeting: MeetingDetail;
  readonly timeZone: string;
}) {
  return (
    <div className="grid gap-5 lg:grid-cols-[2fr_1fr]">
      <Card>
        <CardHeader headingLevel={2} title="Pauta" />
        <p className="mt-3 whitespace-pre-line text-sm text-content">{meeting.agenda}</p>

        {meeting.agendaItems.length > 0 ? (
          <ol className="mt-4 flex flex-col gap-2">
            {meeting.agendaItems.map((item) => (
              <li
                key={item.id}
                className="rounded-md border border-border bg-surface-sunken px-3 py-2.5"
              >
                <p className="text-sm font-medium text-content">
                  <span className="mr-2 text-content-subtle qc-tabular">{item.position}.</span>
                  {item.title}
                </p>
                {item.description ? (
                  <p className="mt-1 text-sm text-content-muted">{item.description}</p>
                ) : null}
                {item.resolution ? (
                  <p className="mt-1 text-sm text-content">
                    <span className="font-semibold">Encaminhamento: </span>
                    {item.resolution}
                  </p>
                ) : null}
              </li>
            ))}
          </ol>
        ) : null}
      </Card>

      <Card>
        <CardHeader headingLevel={2} title="Dados" />
        <dl className="mt-3 grid grid-cols-[9rem_1fr] gap-x-4 gap-y-2 text-sm">
          <DefinitionRow label="Situação" value={MEETING_STATUS_LABEL[meeting.status]} />
          <DefinitionRow label="Tipo" value={meeting.typeLabel} />
          <DefinitionRow label="Local" value={meeting.location} />
          <DefinitionRow
            label="Início"
            value={formatRegulatedDateTime(meeting.startsAt, timeZone)}
          />
          <DefinitionRow
            label="Término"
            value={formatRegulatedDateTime(meeting.endsAt, timeZone)}
          />
          <DefinitionRow label="Organizador" value={meeting.organizerName ?? '—'} />
        </dl>

        {meeting.followUpLog ? (
          <div className="mt-4 rounded-md border border-warning bg-warning-subtle px-3 py-2.5">
            <p className="text-xs font-semibold uppercase tracking-wide text-warning-content">
              Registro da SACAP de follow-up
            </p>
            {/*
              O mesmo registro aparece na reunião de origem, que NÃO nasceu de
              reprovação nenhuma — só a de follow-up recebe a explicação.
            */}
            {meeting.isFollowUp ? (
              <p className="mt-1 text-sm text-warning-content">
                Esta reunião foi criada pela reprovação de eficácia de uma ação corretiva.
              </p>
            ) : null}
            <p className="mt-1 whitespace-pre-line text-sm text-warning-content">
              {meeting.followUpLog}
            </p>
          </div>
        ) : null}

        {meeting.originMeetingId ? (
          <div className="mt-3">
            <Button asChild variant="secondary" size="sm">
              <Link to="/reunioes/$meetingId" params={{ meetingId: meeting.originMeetingId }}>
                Abrir a reunião de origem
              </Link>
            </Button>
          </div>
        ) : null}
      </Card>
    </div>
  );
}

function ParticipantsTab({
  meeting,
  canManage,
  busy,
  onSetAttendance,
}: {
  readonly meeting: MeetingDetail;
  readonly canManage: boolean;
  readonly busy: boolean;
  readonly onSetAttendance: (userId: string, status: AttendanceStatus) => void;
}) {
  if (meeting.participants.length === 0) {
    return (
      <EmptyState
        icon={<IconUsers size={22} />}
        title="Nenhum participante"
        description="Uma reunião sem participantes não produz ata nem evidência de análise crítica — o encerramento é recusado pelo servidor."
      />
    );
  }

  return (
    <Card padding="none">
      <div className="p-4">
        <CardHeader
          headingLevel={2}
          title={`Participantes (${meeting.participants.length})`}
          description="A confirmação é resposta ao convite; a presença efetiva é apurada por quem conduz, na abertura da reunião. São coisas diferentes e ficam registradas em separado."
        />
      </div>
      <ul className="border-t border-border">
        {meeting.participants.map((participant) => (
          <li
            key={participant.id}
            className="flex flex-wrap items-center gap-3 border-b border-border-subtle px-4 py-3 last:border-b-0"
          >
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-content">
                {participant.userName ?? 'Colaborador removido'}
              </p>
              {participant.note ? (
                <p className="truncate text-xs text-content-subtle">{participant.note}</p>
              ) : null}
            </div>

            <AttendanceStatusBadge status={participant.status} />

            {canManage ? (
              <div
                role="group"
                aria-label={`Registrar presença de ${participant.userName ?? 'participante'}`}
                className="flex shrink-0 items-center gap-1"
              >
                <Button
                  size="sm"
                  variant={participant.status === AttendanceStatus.ATTENDED ? 'primary' : 'ghost'}
                  disabled={busy}
                  aria-pressed={participant.status === AttendanceStatus.ATTENDED}
                  onClick={() => onSetAttendance(participant.userId, AttendanceStatus.ATTENDED)}
                >
                  Presente
                </Button>
                <Button
                  size="sm"
                  variant={participant.status === AttendanceStatus.ABSENT ? 'primary' : 'ghost'}
                  disabled={busy}
                  aria-pressed={participant.status === AttendanceStatus.ABSENT}
                  onClick={() => onSetAttendance(participant.userId, AttendanceStatus.ABSENT)}
                >
                  Ausente
                </Button>
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    </Card>
  );
}

function LinkedNonconformitiesTab({ meeting }: { readonly meeting: MeetingDetail }) {
  if (meeting.nonconformities.length === 0) {
    return (
      <EmptyState
        icon={<IconAlertTriangle size={22} />}
        title="Nenhuma não-conformidade vinculada"
        description={
          meeting.requiresEffectiveness
            ? 'Uma SACAP precisa tratar ao menos uma não-conformidade. Sem vínculo o encerramento é recusado pelo servidor.'
            : 'Esta reunião não trata não-conformidades. Reuniões de rotina e análises críticas pela direção não exigem vínculo.'
        }
      />
    );
  }

  return (
    <ul aria-label="Não-conformidades tratadas nesta reunião" className="flex flex-col gap-2">
      {meeting.nonconformities.map((item) => (
        <li key={item.id}>
          <Link
            to="/nao-conformidades/$nonconformityId"
            params={{ nonconformityId: item.nonconformityId }}
            className="flex flex-wrap items-center gap-3 rounded-md border border-border bg-surface px-3 py-2.5 hover:bg-surface-sunken"
          >
            <span className="font-mono text-sm font-medium text-brand">{item.code}</span>
            <span className="min-w-0 flex-1 truncate text-sm text-content">{item.title}</span>
            <NonconformityStatusBadge status={item.status} />
            {item.inherited ? (
              <Badge tone="warning" size="sm">
                Herdada da reunião de origem
              </Badge>
            ) : null}
          </Link>
        </li>
      ))}
    </ul>
  );
}

function IshikawaTab({
  meeting,
  activeNcId,
  onSelectNc,
  groups,
  loading,
  hasCase,
  readOnly,
  busy,
  onAdd,
  onReprioritize,
  onPromote,
  onDelete,
}: {
  readonly meeting: MeetingDetail;
  readonly activeNcId: string | null;
  readonly onSelectNc: (id: string) => void;
  readonly groups: ComponentProps<typeof IshikawaBoard>['groups'];
  readonly loading: boolean;
  readonly hasCase: boolean;
  readonly readOnly: boolean;
  readonly busy: boolean;
  readonly onAdd: (category: IshikawaCategory) => void;
  readonly onReprioritize: (causeId: string, priority: number) => void;
  readonly onPromote: NonNullable<ComponentProps<typeof IshikawaBoard>['onPromote']>;
  readonly onDelete: NonNullable<ComponentProps<typeof IshikawaBoard>['onDelete']>;
}) {
  if (meeting.nonconformities.length === 0) {
    return (
      <EmptyState
        icon={<IconAlertTriangle size={22} />}
        title="Sem não-conformidade para analisar"
        description="A análise de causa acontece sobre uma não-conformidade procedente. Vincule ao menos um registro à reunião para registrar o Ishikawa."
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {meeting.nonconformities.length > 1 ? (
        <Card padding="sm">
          <div className="max-w-lg">
            <Field
              label="Não-conformidade em análise"
              description="Cada não-conformidade tem o próprio diagrama de causas. Escolha qual está sendo analisada agora."
            >
              <Select
                value={activeNcId ?? undefined}
                onValueChange={onSelectNc}
                options={meeting.nonconformities.map((item) => ({
                  value: item.nonconformityId,
                  label: `${item.code} — ${item.title}`,
                }))}
              />
            </Field>
          </div>
        </Card>
      ) : null}

      {loading ? (
        <LoadingRegion loading label="Carregando a análise de causa…">
          <Skeleton className="h-64 w-full" />
        </LoadingRegion>
      ) : !hasCase ? (
        <EmptyState
          icon={<IconInfo size={22} />}
          title="O caso CAPA ainda não existe"
          description="A análise de causa começa quando a não-conformidade é considerada procedente pelo gestor, em ato assinado. Até lá não há onde registrar as causas."
        />
      ) : (
        <IshikawaBoard
          groups={groups}
          readOnly={readOnly}
          busy={busy}
          onAdd={readOnly ? undefined : onAdd}
          onReprioritize={
            readOnly ? undefined : (causeId, priority) => onReprioritize(causeId, priority)
          }
          onPromote={readOnly ? undefined : onPromote}
          onDelete={readOnly ? undefined : onDelete}
        />
      )}
    </div>
  );
}

function MinutesTab({
  meeting,
  timeZone,
}: {
  readonly meeting: MeetingDetail;
  readonly timeZone: string;
}) {
  if (meeting.minutes.length === 0) {
    return (
      <EmptyState
        icon={<IconCheckCircle size={22} />}
        title="A ata ainda não foi emitida"
        description="A ata é emitida no encerramento da reunião, junto com a assinatura eletrônica. O texto é congelado no ato e não pode ser alterado depois."
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {meeting.minutes.map((entry) => (
        <Card key={entry.id}>
          <CardHeader
            headingLevel={2}
            title="Ata da reunião"
            description={`Emitida em ${formatRegulatedDateTime(entry.issuedAt, timeZone)}.`}
          />
          <p className="mt-3 whitespace-pre-line text-sm text-content">{entry.content}</p>
          <p className="mt-3 text-xs text-content-subtle">
            Assinatura: <span className="font-mono">{truncateHash(entry.signatureId, 8, 6)}</span>
          </p>
        </Card>
      ))}

      {meeting.effectivenessNote ? (
        <Card className="border-l-4 border-l-brand">
          <CardHeader
            headingLevel={2}
            title={`Eficácia da SACAP: ${meeting.effective ? 'eficaz' : 'não eficaz'}`}
            description={
              meeting.effectivenessAt
                ? `Registrada em ${formatRegulatedDateTime(meeting.effectivenessAt, timeZone)}.`
                : undefined
            }
          />
          <p className="mt-3 whitespace-pre-line text-sm text-content">
            {meeting.effectivenessNote}
          </p>
        </Card>
      ) : null}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Diálogos
// ─────────────────────────────────────────────────────────────────────────────

/** `datetime-local` espera `YYYY-MM-DDTHH:mm` sem fuso. */
function toLocalInput(iso: string): string {
  const date = new Date(iso);
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
}

/**
 * EDIÇÃO DA REUNIÃO — o chamador que `PATCH /meetings/:id` não tinha.
 *
 * Sem esta tela não havia como corrigir pauta, local, horário nem participantes
 * depois de agendada, e a guarda anti-wipe do BUG-REU-07 — destaque da Onda 2 —
 * nunca era exercida pelo produto.
 *
 * O formulário envia SÓ os campos alterados. Não é otimização: é a guarda. Um
 * formulário que reenvia tudo o que carregou é exatamente o `save()` do legado,
 * que mandava o POST inteiro e apagava o que não estivesse no formulário.
 */
function MeetingEditDialog({
  open,
  onOpenChange,
  meeting,
  busy,
  people,
  onConfirm,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly meeting: MeetingDetail;
  readonly busy: boolean;
  readonly people: ReadonlyArray<{ id: string; name: string; hint?: string | undefined }>;
  readonly onConfirm: (input: UpdateMeetingInput) => void;
}) {
  const [subject, setSubject] = useState(meeting.subject);
  const [location, setLocation] = useState(meeting.location);
  const [startsAt, setStartsAt] = useState(toLocalInput(meeting.startsAt));
  const [endsAt, setEndsAt] = useState(toLocalInput(meeting.endsAt));
  const [agenda, setAgenda] = useState(meeting.agenda);
  const [participantUserIds, setParticipantUserIds] = useState<string[]>(
    meeting.participants
      .filter((row) => row.status !== AttendanceStatus.ABSENT)
      .map((row) => row.userId),
  );

  // Reabrir o diálogo recarrega do registro: um rascunho velho na memória do
  // componente faria a tela salvar por cima de uma alteração de outra pessoa.
  const reset = () => {
    setSubject(meeting.subject);
    setLocation(meeting.location);
    setStartsAt(toLocalInput(meeting.startsAt));
    setEndsAt(toLocalInput(meeting.endsAt));
    setAgenda(meeting.agenda);
    setParticipantUserIds(
      meeting.participants
        .filter((row) => row.status !== AttendanceStatus.ABSENT)
        .map((row) => row.userId),
    );
  };

  const invalidWindow =
    startsAt !== '' && endsAt !== '' && new Date(endsAt) <= new Date(startsAt);
  const noParticipants = participantUserIds.length === 0;

  const originalParticipants = meeting.participants
    .filter((row) => row.status !== AttendanceStatus.ABSENT)
    .map((row) => row.userId)
    .sort()
    .join(',');

  const submit = () => {
    const input: Record<string, unknown> = {};
    if (subject !== meeting.subject) input.subject = subject;
    if (location !== meeting.location) input.location = location;
    if (agenda !== meeting.agenda) input.agenda = agenda;
    if (startsAt !== toLocalInput(meeting.startsAt)) input.startsAt = new Date(startsAt);
    if (endsAt !== toLocalInput(meeting.endsAt)) input.endsAt = new Date(endsAt);
    if ([...participantUserIds].sort().join(',') !== originalParticipants) {
      input.participantUserIds = participantUserIds;
    }
    // `nonconformityIds` NUNCA entra aqui — ver o comentário da mutação.
    onConfirm(input as UpdateMeetingInput);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (busy) return;
        if (next) reset();
        onOpenChange(next);
      }}
      size="lg"
      title="Editar a reunião"
      description="Corrija assunto, local, horário, pauta ou participantes. As não-conformidades vinculadas não mudam por aqui: continuam as mesmas depois de salvar."
      dismissible={!busy}
      footer={
        <>
          <Button variant="secondary" disabled={busy} onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={invalidWindow || noParticipants || subject.trim() === ''}
            onClick={submit}
          >
            Salvar alterações
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Assunto" required>
          <Input
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
            maxLength={255}
            disabled={busy}
          />
        </Field>
        <Field label="Local" required>
          <Input
            value={location}
            onChange={(event) => setLocation(event.target.value)}
            maxLength={255}
            disabled={busy}
          />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Início" required>
            <Input
              type="datetime-local"
              value={startsAt}
              onChange={(event) => setStartsAt(event.target.value)}
              disabled={busy}
            />
          </Field>
          <Field
            label="Término"
            required
            error={invalidWindow ? 'O término precisa ser posterior ao início.' : undefined}
          >
            <Input
              type="datetime-local"
              value={endsAt}
              onChange={(event) => setEndsAt(event.target.value)}
              disabled={busy}
            />
          </Field>
        </div>
        <Field label="Pauta" required>
          <Textarea
            value={agenda}
            onChange={(event) => setAgenda(event.target.value)}
            rows={6}
            maxLength={20_000}
            showCounter
            disabled={busy}
          />
        </Field>
        <PeoplePicker
          userIds={participantUserIds}
          onUserIdsChange={setParticipantUserIds}
          users={people}
          disabled={busy}
          noun="participantes"
          userFieldLabel="Participantes"
          userFieldDescription="Quem for removido fica registrado como ausente, não é apagado: o convite continua no histórico da reunião. Quem for reconvidado precisa confirmar presença de novo."
          error={noParticipants ? 'A reunião precisa de ao menos um participante.' : undefined}
        />
      </div>
    </Dialog>
  );
}

/**
 * CANCELAMENTO — §B.1 métodos 2 e 3 (`justifica()` e `cancelar()`).
 *
 * O legado tinha um fragmento de tela só para pedir a justificativa e exigia o
 * texto apenas no JavaScript; o POST aceitava vazio. Aqui o mínimo de 10
 * caracteres é do contrato e do servidor.
 */
function MeetingCancelDialog({
  open,
  onOpenChange,
  subject,
  busy,
  onConfirm,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly subject: string;
  readonly busy: boolean;
  readonly onConfirm: (justification: string) => void;
}) {
  const [justification, setJustification] = useState('');
  const tooShort = justification.trim().length < 10;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (busy) return;
        if (next) setJustification('');
        onOpenChange(next);
      }}
      title="Cancelar a reunião"
      description={`"${subject}" deixará de constar entre as reuniões ativas. Os participantes serão notificados com a justificativa.`}
      dismissible={!busy}
      footer={
        <>
          <Button variant="secondary" disabled={busy} onClick={() => onOpenChange(false)}>
            Voltar
          </Button>
          <Button
            variant="danger"
            loading={busy}
            disabled={tooShort}
            onClick={() => onConfirm(justification.trim())}
          >
            Confirmar cancelamento
          </Button>
        </>
      }
    >
      <Field
        label="Justificativa"
        required
        description="Obrigatória. Fica no registro da reunião e na trilha de auditoria, e é o texto que os participantes recebem."
        error={
          justification.length > 0 && tooShort
            ? 'Descreva o motivo em pelo menos 10 caracteres.'
            : undefined
        }
      >
        <Textarea
          value={justification}
          onChange={(event) => setJustification(event.target.value)}
          rows={4}
          maxLength={4000}
          showCounter
          disabled={busy}
        />
      </Field>
    </Dialog>
  );
}

function ClosureDialog({
  open,
  onOpenChange,
  meeting,
  onConfirm,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly meeting: MeetingDetail;
  readonly onConfirm: (minutes: string) => void;
}) {
  const [minutes, setMinutes] = useState('');

  const noParticipants = meeting.participants.length === 0;
  const sacapWithoutNc =
    meeting.requiresNonconformity && meeting.nonconformities.length === 0;
  const blocked = noParticipants || sacapWithoutNc;
  const emptyMinutes = minutes.trim().length === 0;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title="Encerrar a reunião e emitir a ata"
      description="O encerramento é ato assinado. A ata é congelada no momento da assinatura e passa a integrar o registro — não há edição posterior."
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={blocked || emptyMinutes}
            onClick={() => onConfirm(minutes.trim())}
          >
            Continuar para a assinatura
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {blocked ? (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-md border border-danger bg-danger-subtle px-3 py-2.5 text-sm text-danger-content"
          >
            <IconAlertTriangle size={15} aria-hidden="true" className="mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold">O encerramento está bloqueado</p>
              <ul className="mt-1 list-inside list-disc">
                {noParticipants ? (
                  <li>
                    A reunião não tem participantes. Uma reunião sem participantes não produz ata
                    nem evidência de análise crítica.
                  </li>
                ) : null}
                {sacapWithoutNc ? (
                  <li>
                    Uma SACAP precisa estar vinculada a ao menos uma não-conformidade. A SACAP
                    existe para tratar não-conformidades — sem vínculo, não há o que analisar.
                  </li>
                ) : null}
              </ul>
            </div>
          </div>
        ) : null}

        <div className="rounded-md border border-border bg-surface-sunken px-3 py-2.5 text-sm text-content-muted">
          <p>
            Presenças registradas:{' '}
            <span className="font-medium text-content">
              {meeting.participants.filter((p) => p.status === AttendanceStatus.ATTENDED).length}{' '}
              presentes
            </span>
            ,{' '}
            {meeting.participants.filter((p) => p.status === AttendanceStatus.ABSENT).length}{' '}
            ausentes. Ajuste na aba Participantes antes de encerrar — a apuração vai junto da ata.
          </p>
        </div>

        <Field
          label="Ata da reunião"
          required
          description="O que foi discutido, as decisões e os encaminhamentos. É o registro que responde “a análise crítica aconteceu?”."
        >
          <Textarea
            value={minutes}
            onChange={(event) => setMinutes(event.target.value)}
            rows={10}
            maxLength={65_535}
            disabled={blocked}
          />
        </Field>

        {meeting.requiresNonconformity ? (
          <p className="flex items-start gap-2 rounded-md border border-info bg-info-subtle px-3 py-2.5 text-sm text-info-content">
            <IconInfo size={15} aria-hidden="true" className="mt-0.5 shrink-0" />
            <span>
              O tipo desta reunião exige avaliação de eficácia: ao encerrar, ela passa a{' '}
              <strong className="font-semibold">aguardar a avaliação</strong>.
            </span>
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}

function EffectivenessDialog({
  open,
  onOpenChange,
  busy,
  onConfirm,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly busy: boolean;
  readonly onConfirm: (params: {
    readonly effective: boolean;
    readonly justification: string;
  }) => void;
}) {
  const [effective, setEffective] = useState<'yes' | 'no'>('yes');
  const [justification, setJustification] = useState('');
  const missing = justification.trim().length === 0;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && busy) return;
        onOpenChange(next);
      }}
      dismissible={!busy}
      title="Avaliar a eficácia da SACAP"
      description="A justificativa é obrigatória nos dois caminhos: é o registro de que a análise crítica aconteceu."
      footer={
        <>
          <Button variant="secondary" disabled={busy} onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={missing}
            onClick={() =>
              onConfirm({ effective: effective === 'yes', justification: justification.trim() })
            }
          >
            Registrar avaliação
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <RadioGroup<'yes' | 'no'>
          legend="A SACAP foi eficaz?"
          value={effective}
          onValueChange={setEffective}
          options={[
            {
              value: 'yes',
              label: 'Eficaz',
              description: 'A análise e o plano de ação resolveram o problema tratado.',
            },
            {
              value: 'no',
              label: 'Não eficaz',
              description:
                'O problema persiste. Reprovar AQUI não cria reunião nova: a SACAP de follow-up nasce da reprovação de eficácia de uma AÇÃO corretiva e é agendada automaticamente.',
            },
          ]}
        />

        <Field
          label="Justificativa"
          required
          error={missing && justification.length > 0 ? 'Informe a justificativa.' : undefined}
        >
          <Textarea
            value={justification}
            onChange={(event) => setJustification(event.target.value)}
            rows={5}
            maxLength={4000}
            showCounter
          />
        </Field>
      </div>
    </Dialog>
  );
}

function RootCauseDialog({
  open,
  category,
  busy,
  nextPriority,
  onOpenChange,
  onConfirm,
}: {
  readonly open: boolean;
  readonly category: IshikawaCategory | null;
  readonly busy: boolean;
  readonly nextPriority: number;
  readonly onOpenChange: (open: boolean) => void;
  readonly onConfirm: (params: {
    readonly category: IshikawaCategory;
    readonly description: string;
    readonly priority: number;
    readonly isRootCause: boolean;
  }) => void;
}) {
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState(String(nextPriority));
  const [isRootCause, setIsRootCause] = useState(false);

  const label = category ? ISHIKAWA_CATEGORY_LABEL_PT_BR[category] : '';
  const empty = description.trim().length === 0;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && busy) return;
        if (next) {
          setDescription('');
          setPriority(String(nextPriority));
          setIsRootCause(false);
        }
        onOpenChange(next);
      }}
      dismissible={!busy}
      title={`Nova causa em ${label}`}
      description="A categoria é uma das seis do diagrama (6M), não texto livre."
      footer={
        <>
          <Button variant="secondary" disabled={busy} onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={empty || !category}
            onClick={() => {
              if (!category) return;
              onConfirm({
                category,
                description: description.trim(),
                priority: Number(priority) || 1,
                isRootCause,
              });
            }}
          >
            Registrar causa
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Descrição da causa" required>
          <Textarea
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            rows={4}
            maxLength={512}
            showCounter
            autoFocus
          />
        </Field>

        <Field
          label="Prioridade dentro da categoria"
          description="1 é a mais prioritária."
        >
          <NumberInput
            value={priority}
            onChange={(event) => setPriority(event.target.value)}
            min={1}
            max={999}
            className="w-28"
          />
        </Field>

        <Checkbox
          checked={isRootCause}
          onCheckedChange={setIsRootCause}
          label="Causa-raiz confirmada"
          description="Marque quando a investigação (por exemplo, os cinco porquês) confirmou que esta é a causa fundamental, e não um sintoma intermediário."
        />
      </div>
    </Dialog>
  );
}

function DefinitionRow({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <>
      <dt className="text-content-subtle">{label}</dt>
      <dd className="min-w-0 break-words text-content">{value}</dd>
    </>
  );
}
